import { Component } from '@/types'
import { getCartValidationData, cartIsMixed } from '@/utils/cart-validation'

async function removeVariant(variantId: number): Promise<boolean> {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // /cart/change.js validates `id` against a string pattern (variant id or
    // line key); a JSON *number* is rejected with 400 "expected Integer to be
    // a <regex>", so the item never gets removed. Send it as a string.
    body: JSON.stringify({ id: String(variantId), quantity: 0 }),
  })

  if (!res.ok) {
    console.error(
      '[mixed-cart-modal] failed to remove variant',
      variantId,
      res.status,
      await res.text().catch(() => '')
    )
  }

  return res.ok
}

async function refreshCartValidationData() {
  const res = await fetch('/cart')
  const html = await res.text()
  const newDoc = new DOMParser().parseFromString(html, 'text/html')
  const fresh = newDoc.getElementById('cart-validation-data')
  const current = document.getElementById('cart-validation-data')
  if (fresh && current) current.textContent = fresh.textContent
}

async function keepOnly(keepPreorder: boolean) {
  const data = getCartValidationData()
  if (!data) return

  hideModal()

  const toRemove = data.items.filter(item =>
    keepPreorder ? !item.is_preorder : item.is_preorder
  )

  let allRemoved = true
  for (const item of toRemove) {
    const removed = await removeVariant(item.variant_id)
    if (!removed) allRemoved = false
  }

  // Refresh validation data BEFORE dispatching the update event so any
  // listener (including our own re-check) sees the cleaned cart state.
  await refreshCartValidationData()

  // If a removal failed the cart is still mixed. Dispatching cart.updated
  // would re-run checkAndShow() and flash the modal straight back up with no
  // explanation — trapping the customer again. Bail out (modal already hidden)
  // and rely on the logged error above instead of silently re-looping.
  if (!allRemoved) return

  window.dispatchEvent(new CustomEvent('se-event.cart.updated'))
}

function showModal() {
  const modal = document.querySelector(
    '[data-ref="mixed-cart-modal"]'
  ) as HTMLElement | null
  if (modal) modal.style.display = 'block'
}

function hideModal() {
  const modal = document.querySelector(
    '[data-ref="mixed-cart-modal"]'
  ) as HTMLElement | null
  if (modal) modal.style.display = 'none'
}

let listenersBound = false

function checkAndShow() {
  if (cartIsMixed()) showModal()
}

const component: Component = ref => {
  if (!ref.mixedCartModal) return

  if (listenersBound) {
    checkAndShow()
    return
  }
  listenersBound = true

  const modal = ref.mixedCartModal[0] as HTMLElement
  modal
    .querySelector('[data-ref="mixed-cart-keep-preorder"]')
    ?.addEventListener('click', () => keepOnly(true))
  modal
    .querySelector('[data-ref="mixed-cart-keep-regular"]')
    ?.addEventListener('click', () => keepOnly(false))

  // cart-validation-data in the DOM is stale after any cart mutation until
  // main-cart's refreshCartValidationData() refetches it; that helper fires
  // `se-event.cart.updated` afterwards, so we re-check then.
  window.addEventListener('se-event.cart.updated', checkAndShow)
  window.addEventListener('se-event.cart.product-added', checkAndShow)

  checkAndShow()
}

export default component
